import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormControl, Validators } from '@angular/forms'
import { Store, select } from '@ngrx/store'
import { Subject } from 'rxjs'
import { distinctUntilChanged, finalize, filter, take, takeUntil } from 'rxjs/operators'
import {
  LoadPaymentGateways,
  PaymentGateway,
  selectAllEnabledPaymentGateways,
  selectPaymentGatewaysLoaded,
} from '@tokeet-frontend/gateways'
import {
  ConfirmDialogService,
  Toaster,
  Currency,
  getCurrencyByCode,
  currenciesWithoutIcon,
} from '@tokeet-frontend/tv3-platform'
import { SecurityDepositsService, SecurityDepositLog } from '@tv3/store/security-deposits/security-deposits.service'
import { SecurityDeposit, SecurityDepositStatus } from '@tv3/store/security-deposits/security-deposits.model'
import { FetchInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { selectInquiriesByIds } from '@tv3/store/inquiry/inquiry.selectors'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { AmplitudeService } from '@tv3/services/amplitude.service'

interface SecurityDepositOverlayModel {
  deposit: SecurityDeposit
}

@Component({
  selector: 'app-security-deposit-overlay',
  templateUrl: './security-deposit-overlay.component.html',
  styleUrls: ['./security-deposit-overlay.component.scss'],
})
export class SecurityDepositOverlayComponent implements OnInit, OnDestroy {
  tab: 'deposit' | 'logs' = 'deposit'

  readonly deposit: SecurityDeposit
  paymentGateways: PaymentGateway[] = []
  gatewayControl = new FormControl(null, { validators: [Validators.required] })
  isUpdatingGateway = false
  allowGatewayChange = false
  isSending = false
  isCapturing = false
  isRefunding = false
  isCanceling = false
  logs: SecurityDepositLog[] = []
  isLogsLoading = false
  logsLoaded = false
  logsError: string | null = null

  guestName = ''
  guestArriveEpoch: number | null = null
  guestDepartEpoch: number | null = null
  private inquiry: Inquiry | undefined

  private destroy$ = new Subject<void>()
  private readonly currencySymbolMap = new Map<string, string>(
    currenciesWithoutIcon.map((currency) => [currency.code.toLowerCase(), currency.symbol])
  )

  constructor(
    private dialogRef: MatDialogRef<SecurityDepositOverlayComponent>,
    @Inject(MAT_DIALOG_DATA) private data: SecurityDepositOverlayModel,
    private store: Store,
    private depositsService: SecurityDepositsService,
    private toaster: Toaster,
    private amp: AmplitudeService,
    private confirm: ConfirmDialogService
  ) {
    this.deposit = data.deposit
    console.log('Deposit in constructor:', this.deposit)
    console.log('Tab value in constructor:', this.tab)
  }

  ngOnInit() {
    console.log('Tab value in ngOnInit:', this.tab)
    // Explicitly set tab to ensure it's initialized
    this.tab = 'deposit'
    console.log('Tab value after explicit set:', this.tab)

    this.initializeGatewaySelector()
    this.initializeGuestDetails()
    this.loadInquiryDetails()
  }

  setTab(tab: 'deposit' | 'logs') {
    this.tab = tab
    if (tab === 'logs') {
      this.loadLogs()
    }
  }

  onClose() {
    this.dialogRef.close()
  }

  onViewAsGuest() {
    if (!this.deposit) return
    const opened = this.depositsService.viewAsGuest(this.deposit)
    this.amp.logEvent('security-deposit-view')
    if (!opened) {
      this.toaster.error($localize`Unable to open the guest view for this security deposit.`)
    }
  }

  onSendDeposit() {
    if (!this.deposit || this.isSending) return

    this.confirmAndExecute(
      $localize`Send security deposit?`,
      $localize`We will email the guest a security deposit request. Continue?`,
      () => {
        this.isSending = true
        this.depositsService
          .sendDeposit(this.deposit.pkey)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isSending = false))
          )
          .subscribe({
            next: (updatedDeposit) => {
              this.toaster.success($localize`Security deposit sent successfully.`)
              this.applyDepositUpdate(updatedDeposit)
              this.depositsService.notifySummaryChanged()
              this.amp.logEvent('security-deposit-send')
            },
            error: (error) => {
              const message = error?.error?.message || $localize`Unable to send the security deposit.`
              this.toaster.error(message)
            },
          })
      }
    )
  }

  onCaptureDeposit() {
    if (!this.deposit || this.isCapturing) return

    this.confirmAndExecute(
      $localize`Capture security deposit?`,
      $localize`This will capture the authorized funds immediately. Continue?`,
      () => {
        this.isCapturing = true
        this.depositsService
          .captureDeposit(this.deposit.pkey)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isCapturing = false))
          )
          .subscribe({
            next: (updatedDeposit) => {
              this.toaster.success($localize`Security deposit captured successfully.`)
              this.applyDepositUpdate(updatedDeposit)
              this.depositsService.notifySummaryChanged()
              this.amp.logEvent('security-deposit-capture')
            },
            error: (error) => {
              const message = error?.error?.message || $localize`Unable to capture the security deposit.`
              this.toaster.error(message)
            },
          })
      }
    )
  }

  onRefundDeposit() {
    if (!this.deposit || this.isRefunding) return

    this.confirmAndExecute(
      $localize`Refund security deposit?`,
      $localize`Refunding releases captured funds back to the guest. Continue?`,
      () => {
        this.isRefunding = true
        this.depositsService
          .refundDeposit(this.deposit.pkey)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isRefunding = false))
          )
          .subscribe({
            next: (updatedDeposit) => {
              this.toaster.success($localize`Security deposit refunded successfully.`)
              this.applyDepositUpdate(updatedDeposit)
              this.depositsService.notifySummaryChanged()
              this.amp.logEvent('security-deposit-refund')
            },
            error: (error) => {
              const message = error?.error?.message || $localize`Unable to refund the security deposit.`
              this.toaster.error(message)
            },
          })
      }
    )
  }

  onCancelDeposit() {
    if (!this.deposit || this.isCanceling || !this.canCancelDeposit) return

    this.confirmAndExecute(
      $localize`Cancel security deposit?`,
      $localize`This will void the security deposit request. Continue?`,
      () => {
        this.isCanceling = true
        this.depositsService
          .cancelDeposit(this.deposit.pkey)
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isCanceling = false))
          )
          .subscribe({
            next: (updatedDeposit) => {
              this.toaster.success($localize`Security deposit canceled successfully.`)
              this.applyDepositUpdate(updatedDeposit)
              this.depositsService.notifySummaryChanged()
              this.amp.logEvent('security-deposit-cancel')
            },
            error: (error) => {
              const message = error?.error?.message || $localize`Unable to cancel the security deposit.`
              this.toaster.error(message)
            },
          })
      }
    )
  }

  trackLog(_: number, log: SecurityDepositLog) {
    return log.pkey
  }

  getLogInitial(log: SecurityDepositLog): string {
    const source = log.channel || log.service || 'SD'
    return (source || 'S').slice(0, 1).toUpperCase()
  }

  getLogTitle(log: SecurityDepositLog): string {
    if (log.channel) return log.channel
    if (log.service) return log.service
    return $localize`Security Deposit`
  }

  get sendButtonLabel(): string {
    const sentAt = (this.deposit?.attributes as any)?.email_sent_at
    return sentAt ? $localize`Re-Send` : $localize`Send`
  }

  get canSendDeposit(): boolean {
    return this.deposit?.status === 'requires_action'
  }

  get canCaptureDeposit(): boolean {
    return this.deposit?.status === 'authorized'
  }

  get canRefundDeposit(): boolean {
    return this.deposit?.status === 'captured'
  }

  get canCancelDeposit(): boolean {
    return this.deposit?.status === 'authorized'
  }

  getStatusDisplay(status: SecurityDepositStatus | string | undefined): string {
    if (!status) return ''

    const statusMap: Record<SecurityDepositStatus, string> = {
      authorized: $localize`Authorized`,
      captured: $localize`Captured`,
      refunded: $localize`Refunded`,
      canceled: $localize`Canceled`,
      failed: $localize`Failed`,
      requires_action: $localize`Requires Action`,
      missing_gateway: $localize`Missing Gateway`,
    }

    return statusMap[status as SecurityDepositStatus] || status
  }

  getStatusClass(status: SecurityDepositStatus | string | undefined): string {
    if (!status) return 'badge-secondary'

    const statusClasses: Record<SecurityDepositStatus, string> = {
      authorized: 'badge-info',
      captured: 'badge-success',
      refunded: 'badge-secondary',
      canceled: 'badge-warning',
      failed: 'badge-danger',
      requires_action: 'badge-warning',
      missing_gateway: 'badge-danger',
    }

    return statusClasses[status as SecurityDepositStatus] || 'badge-secondary'
  }

  get authorizedTimestamp(): number | null {
    if (this.deposit?.status !== 'authorized') return null
    return this.getTimestampForStatus('authorized')
  }

  get authorizationEndingTimestamp(): number | null {
    const timestamp = this.authorizedTimestamp
    if (timestamp === null) return null
    const sevenDaysInSeconds = 7 * 24 * 60 * 60
    return timestamp + sevenDaysInSeconds
  }

  get statusTimestamp(): number | null {
    const status = this.deposit?.status as SecurityDepositStatus | undefined
    const statusesWithTimestamp: SecurityDepositStatus[] = ['captured', 'refunded', 'canceled']
    if (!status || !statusesWithTimestamp.includes(status)) return null
    return this.getTimestampForStatus(status)
  }

  get depositCurrency(): Currency | undefined {
    if (!this.deposit?.currency) return undefined
    return getCurrencyByCode(this.deposit.currency)
  }

  getCurrencySymbol(code: string | undefined): string {
    if (!code) return '$'

    const normalizedCode = code.toLowerCase()
    return this.currencySymbolMap.get(normalizedCode) ?? code
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private initializeGatewaySelector() {
    this.allowGatewayChange = this.deposit?.status === 'missing_gateway'

    this.store.pipe(select(selectPaymentGatewaysLoaded), take(1)).subscribe((loaded) => {
      if (!loaded) {
        this.store.dispatch(LoadPaymentGateways())
      }
    })

    this.store.pipe(select(selectAllEnabledPaymentGateways), takeUntil(this.destroy$)).subscribe((gateways) => {
      this.paymentGateways = gateways || []
      const currentGatewayId = this.deposit?.gateway_id
      if (currentGatewayId && !this.gatewayControl.value) {
        this.gatewayControl.setValue(currentGatewayId, { emitEvent: false })
      }
    })

    const initialGatewayId = this.deposit?.gateway_id || null
    if (initialGatewayId) {
      this.gatewayControl.setValue(initialGatewayId, { emitEvent: false })
    }

    this.updateGatewayControlState()

    this.gatewayControl.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((gatewayId) => {
      if (!this.allowGatewayChange) {
        this.gatewayControl.setValue(this.deposit?.gateway_id || null, { emitEvent: false })
        return
      }
      if (!gatewayId || gatewayId === this.deposit.gateway_id) return
      this.updateGateway(gatewayId)
    })
  }

  private updateGateway(gatewayId: string) {
    const previousGateway = this.deposit.gateway_id || null
    this.isUpdatingGateway = true
    this.gatewayControl.disable({ emitEvent: false })

    this.depositsService
      .updateDepositGateway(this.deposit.pkey, gatewayId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isUpdatingGateway = false
          this.gatewayControl.enable({ emitEvent: false })
        })
      )
      .subscribe({
        next: (updatedDeposit) => {
          this.deposit.gateway_id = updatedDeposit.gateway_id
          this.deposit.status = updatedDeposit.status
          this.deposit.description = updatedDeposit.description
          this.gatewayControl.setValue(updatedDeposit.gateway_id || null, { emitEvent: false })
          this.toaster.success($localize`Payment gateway updated.`)
          this.amp.logEvent('security-deposit-change-gateway')
          this.applyDepositUpdate(updatedDeposit)
        },
        error: (error) => {
          const message = error?.error?.message || $localize`Unable to update payment gateway.`
          this.toaster.error(message)
          this.gatewayControl.setValue(previousGateway, { emitEvent: false })
        },
      })
  }

  private updateGatewayControlState() {
    this.allowGatewayChange = this.deposit?.status === 'missing_gateway'

    if (this.allowGatewayChange) {
      this.gatewayControl.enable({ emitEvent: false })
    } else {
      this.gatewayControl.disable({ emitEvent: false })
    }
  }

  private initializeGuestDetails() {
    const attrs = (this.deposit?.attributes || {}) as any
    this.guestName = attrs?.guest_name || ''
    this.guestArriveEpoch = this.normalizeEpoch(attrs?.guest_arrive)
    this.guestDepartEpoch = this.normalizeEpoch(attrs?.guest_depart)
  }

  private loadInquiryDetails() {
    const inquiryId = this.deposit?.inquiry_id
    if (!inquiryId) return

    this.store.dispatch(FetchInquiries({ ids: [inquiryId] }))

    this.store
      .pipe(
        select(selectInquiriesByIds([inquiryId])),
        filter((inquiries) => inquiries?.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe((inquiries) => {
        const inquiry = inquiries[0]
        this.inquiry = inquiry
        this.updateGuestDetailsFromInquiry(inquiry)
      })
  }

  private updateGuestDetailsFromInquiry(inquiry: Inquiry) {
    if (!inquiry) return

    const guestName = inquiry?.guestDetails?.name || (inquiry as any)?.guestNameView || ''
    this.guestName = guestName?.trim ? guestName.trim() : guestName

    const arrive = (inquiry as any)?.guestArrive ?? (inquiry as any)?.guest_arrive
    const depart = (inquiry as any)?.guestDepart ?? (inquiry as any)?.guest_depart
    this.guestArriveEpoch = this.normalizeEpoch(arrive)
    this.guestDepartEpoch = this.normalizeEpoch(depart)
  }

  private normalizeEpoch(value: unknown): number | null {
    if (value === null || value === undefined) return null
    if (typeof value === 'number' && Number.isFinite(value)) return value
    const parsed = typeof value === 'string' ? parseFloat(value) : NaN
    return Number.isFinite(parsed) ? parsed : null
  }

  private getTimestampForStatus(status: SecurityDepositStatus | string | undefined): number | null {
    if (!status) return null
    const timestamps = ((this.deposit?.attributes || {}) as any)?.timestamps
    if (!timestamps) return null
    return this.normalizeEpoch(timestamps[status])
  }

  private applyDepositUpdate(updatedDeposit: SecurityDeposit) {
    if (!updatedDeposit) return
    this.deposit.status = updatedDeposit.status
    this.deposit.gateway_id = updatedDeposit.gateway_id
    this.deposit.attributes = updatedDeposit.attributes
    this.deposit.description = updatedDeposit.description

    this.gatewayControl.setValue(updatedDeposit.gateway_id || null, { emitEvent: false })
    this.updateGatewayControlState()

    if (updatedDeposit.attributes) {
      this.initializeGuestDetails()
      if (this.inquiry) {
        this.updateGuestDetailsFromInquiry(this.inquiry)
      }
    }
  }

  private confirmAndExecute(title: string, body: string, execute: () => void) {
    this.confirm
      .confirm({ title, body })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => execute())
  }

  private loadLogs() {
    if (!this.deposit?.pkey || this.logsLoaded) return
    this.isLogsLoading = true
    this.logsError = null

    this.depositsService
      .loadDepositLogs(this.deposit.pkey)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLogsLoading = false
        })
      )
      .subscribe({
        next: (logs) => {
          this.logs = (logs || []).slice().sort((a, b) => (b.created || 0) - (a.created || 0))
          this.logsLoaded = true
        },
        error: (error) => {
          this.logsError = error?.error?.message || $localize`Unable to load deposit logs.`
        },
      })
  }
}
